import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  LinearProgress,
  useMediaQuery,
  useTheme,
  IconButton,
  Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { apiService } from '../services/apiService';

const Dashboard = ({ showSnackbar, isActive, homeRefreshKey }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [dashboardData, setDashboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const loadDashboardData = useCallback(async (background = false) => {
    try {
      if (background) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const dashboardResponse = await apiService.getDashboardData({ debug: true });
      if (dashboardResponse.success) {
        setDashboardData(dashboardResponse.dashboard_data);
      }
    } catch (err) {
      setError('Error al cargar los datos del dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Refrescar al entrar a Home (homeRefreshKey cambia cada vez que se navega a Home)
  useEffect(() => {
    if (isActive) {
      loadDashboardData();
    }
  }, [isActive, homeRefreshKey, loadDashboardData]);

  // Refrescar al volver a la pestaña del navegador
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isActive) {
        loadDashboardData(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isActive, loadDashboardData]);

  // Función para obtener el color del progress bar basado en el porcentaje
  const getProgressColor = (coveragePercentage) => {
    if (coveragePercentage >= 80) return '#4CAF50'; // Verde
    if (coveragePercentage >= 50) return '#FF9800'; // Naranja
    return '#f44336'; // Rojo
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const totalCampaigns = dashboardData.length;

  const totalImplemented = dashboardData.reduce((s, c) => s + (c.implemented_countries || 0), 0);
  const totalPossible = dashboardData.reduce((s, c) => s + (c.total_countries || 0), 0);
  const overallAdoption = totalPossible > 0 ? (totalImplemented / totalPossible) * 100 : 0;

  const toolboxesWithAvg = dashboardData.filter(c => c.avg_implementation_percentage != null);
  const overallAvgImpl = toolboxesWithAvg.length
    ? toolboxesWithAvg.reduce((s, c) => s + c.avg_implementation_percentage, 0) / toolboxesWithAvg.length
    : 0;

  const overallAdoptionColor = getProgressColor(overallAdoption);
  const overallAvgColor = getProgressColor(overallAvgImpl);

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} gutterBottom sx={{ mb: 0 }}>
          Dashboard
        </Typography>
        <Tooltip title="Actualizar datos">
          <span>
            <IconButton
              onClick={() => loadDashboardData(true)}
              disabled={refreshing}
              aria-label="Actualizar datos del dashboard"
            >
              {refreshing ? (
                <CircularProgress size={24} />
              ) : (
                <RefreshIcon />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Card de Total de Campañas */}
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <Card sx={{ backgroundColor: 'primary.main', color: 'white' }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography color="inherit" variant={isMobile ? 'body1' : 'h6'} gutterBottom>
              Total de ToolBox
            </Typography>
            <Typography variant={isMobile ? 'h3' : 'h2'} component="div" sx={{ fontWeight: 'bold' }}>
              {totalCampaigns}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Card de Adopción Global de ToolBox */}
      {totalCampaigns > 0 && (
        <Box sx={{ mb: { xs: 3, sm: 4 } }}>
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom sx={{ fontWeight: 'bold' }}>
                Adopción global de ToolBox
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Visión agregada de todas las ToolBox combinadas.
              </Typography>

              <Box sx={{ mt: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Tooltip
                    title="Pregunta: ¿qué proporción de las combinaciones país × ToolBox posibles ya está implementada? Cálculo: suma de países implementados de todas las ToolBox dividido por la suma de países totales. Pondera por tamaño de cada ToolBox."
                    arrow
                    placement="top"
                  >
                    <Box display="flex" alignItems="center" gap={0.5} sx={{ cursor: 'help' }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        Cobertura global
                      </Typography>
                      <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    </Box>
                  </Tooltip>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: overallAdoptionColor }}>
                    {overallAdoption.toFixed(1)}%
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  ¿Cuántos países × ToolBox están implementados sobre el total posible?
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(overallAdoption, 100)}
                  sx={{
                    height: 12,
                    borderRadius: 6,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': { backgroundColor: overallAdoptionColor, borderRadius: 6 }
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {totalImplemented} de {totalPossible} combinaciones país × ToolBox implementadas
                </Typography>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Tooltip
                    title="Pregunta: en promedio, ¿qué tan profundamente está implementada cada ToolBox? Cálculo: promedio simple del avg_implementation_percentage de cada ToolBox. Mide intensidad/profundidad de implementación, no cobertura."
                    arrow
                    placement="top"
                  >
                    <Box display="flex" alignItems="center" gap={0.5} sx={{ cursor: 'help' }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        Implementación promedio global
                      </Typography>
                      <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    </Box>
                  </Tooltip>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: overallAvgColor }}>
                    {overallAvgImpl.toFixed(1)}%
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  En promedio, ¿qué tan profundo es el nivel de implementación de cada ToolBox?
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(overallAvgImpl, 100)}
                  sx={{
                    height: 8,
                    borderRadius: 5,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': { backgroundColor: overallAvgColor, borderRadius: 5 }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Cards de Campañas con Progress Bars */}
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {dashboardData.map((campaign, index) => {
          const coveragePercentage = campaign.coverage_percentage || 0;
          const avgImpl = campaign.avg_implementation_percentage ?? null;
          const coverageColor = getProgressColor(coveragePercentage);
          const avgColor = avgImpl !== null ? getProgressColor(avgImpl) : coverageColor;

          return (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 3 } }}>
                  <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom sx={{ fontWeight: 'bold' }}>
                    {campaign.campaign_name}
                  </Typography>

                  <Box sx={{ mt: 2, mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Tooltip
                        title="% de países que superaron el umbral: al menos el 50% de sus canales válidos tienen implementation_percentage ≥ 50."
                        arrow
                        placement="top"
                      >
                        <Box display="flex" alignItems="center" gap={0.5} sx={{ cursor: 'help' }}>
                          <Typography variant="body2" color="text.secondary">
                            Cobertura de países
                          </Typography>
                          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                        </Box>
                      </Tooltip>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: coverageColor }}>
                        {coveragePercentage.toFixed(1)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={coveragePercentage}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        backgroundColor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: coverageColor,
                          borderRadius: 5
                        }
                      }}
                    />
                  </Box>

                  {avgImpl !== null && (
                    <Box sx={{ mt: 1.5, mb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Tooltip
                          title="Promedio aritmético de todos los valores de implementation_percentage de los canales válidos en todos los países de esta ToolBox."
                          arrow
                          placement="top"
                        >
                          <Box display="flex" alignItems="center" gap={0.5} sx={{ cursor: 'help' }}>
                            <Typography variant="body2" color="text.secondary">
                              Implementación promedio
                            </Typography>
                            <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                          </Box>
                        </Tooltip>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: avgColor }}>
                          {avgImpl.toFixed(1)}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(avgImpl, 100)}
                        sx={{
                          height: 6,
                          borderRadius: 5,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': { backgroundColor: avgColor, borderRadius: 5 }
                        }}
                      />
                    </Box>
                  )}
                  <Tooltip
                    title="Un país cuenta como implementado cuando ≥50% de sus canales válidos tienen un valor ≥ 50."
                    arrow
                    placement="bottom"
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, cursor: 'help', display: 'inline-block' }}>
                      {campaign.implemented_countries} de {campaign.total_countries} países implementados
                    </Typography>
                  </Tooltip>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {dashboardData.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No hay ToolBox disponibles
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;
